import {
  characterFamilyKey,
  familyManifest,
  layerTransform,
  resolveLayer,
  selectedLayers,
  type AssetLayer,
} from '../character/manifest';
import BookwormApprovedArt from './BookwormApprovedArt';
import type {
  CharacterAppearance,
  CharacterBaseType,
  CharacterGender,
} from '../types';

type Props = {
  baseType: CharacterBaseType;
  appearance: CharacterAppearance;
  label: string;
};

export default function CharacterArtThumbnail({
  baseType,
  appearance,
  label,
}: Props) {
  const family = characterFamilyKey(baseType, appearance);
  if (family === 'bookworm') {
    return (
      <figure className="approval-character-card">
        <div className="approval-character-canvas" aria-label={label}>
          <BookwormApprovedArt appearance={appearance} />
        </div>
        <figcaption>{label}</figcaption>
      </figure>
    );
  }
  const familyConfig = familyManifest(family);
  const gender =
    familyConfig.gender_art_ready === false
      ? undefined
      : (appearance.gender as CharacterGender | undefined);
  const layers = selectedLayers(baseType, appearance);
  const toneLayer = layers.find(layer => layer.option_type === 'tone');
  const expressionLayers = [
    resolveLayer(family, 'eyes', 'friendly'),
    resolveLayer(family, 'mouth', 'friendly'),
  ].filter((layer): layer is AssetLayer => Boolean(layer));

  return (
    <figure className="approval-character-card">
      <div className="approval-character-canvas" aria-label={label}>
        {[...layers, ...expressionLayers]
          .sort((a, b) => a.z_index - b.z_index)
          .map(layer => (
            <svg
              key={[
                layer.family,
                layer.option_type,
                layer.option_value,
                layer.layer_file,
              ].join(':')}
              className="approval-character-layer"
              viewBox="0 0 320 360"
              aria-hidden="true"
              style={{
                zIndex: layer.z_index,
                color:
                  layer.option_type === 'body_detail'
                    ? toneLayer?.color || '#92775e'
                    : layer.color || '#9aabb7',
              }}
            >
              {family === 'owl' && layer.option_type === 'body' ? (
                <image
                  href="/character-assets/owl-approved-static.webp"
                  x="20"
                  y="20"
                  width="280"
                  height="280"
                  preserveAspectRatio="xMidYMid meet"
                  transform={layerTransform(family, layer, gender)}
                />
              ) : (
                <use
                  href={layer.layer_file}
                  transform={layerTransform(family, layer, gender)}
                />
              )}
            </svg>
          ))}
      </div>
      <figcaption>{label}</figcaption>
    </figure>
  );
}
